/**
 * LaneLightPicker - where a sequencer or macro LIGHT lane points: one fixture
 * (or one of its zones) and one thing it controls.
 *
 * A lane drives exactly one named capability on one patched fixture
 * (OPBridge's `DmxLaneTarget`, DEC-103), so this is deliberately narrower
 * than `LightTargetPicker`, which also offers "All lights" and groups: a lane
 * has no group form on the wire, and offering one would author a target the
 * engine cannot run.
 *
 * ## Two selects, and why these two
 *
 * "Light" is one flat list - each fixture, then its zones beneath it - for
 * the same reason `LightTargetPicker` is one list: a zone is a place, not a
 * sub-question. "Controls" lists only what the chosen fixture or zone can
 * actually do, in plain words (Brightness, Red, Pan), so a novice cannot pick
 * a capability the light does not have.
 *
 * ## A removed light stays selected and says so
 *
 * Same rule as `LightTargetPicker`: silently repointing a lane whose fixture
 * was deleted would turn a broken lane into a working one aimed somewhere
 * else. The stale value stays as a disabled "light removed" option.
 */

import { For, Show } from 'solid-js';
import type { FixtureCapabilityName, LaneDmxTarget } from '@opbuttons/schema';
import { fixtures, fixtureZones, fixtureCapabilityNames } from '../fixtures/store.ts';

/** Look a fixture up in the SAME list the picker offers. Not `getFixture`,
 *  which searches only the local library: once the bridge's fixture list is
 *  authoritative, `fixtures()` is the bridge's, and a lookup against the
 *  local copy would call every real fixture "light removed". */
export function findLaneFixture(id: string) {
  return fixtures().find((f) => f.id === id);
}

const CAP_LABEL: Record<string, string> = {
  intensity: 'Brightness',
  'color.r': 'Red',
  'color.g': 'Green',
  'color.b': 'Blue',
  colorPreset: 'Colour preset',
  strobe: 'Strobe',
  pan: 'Pan',
  tilt: 'Tilt',
  gobo: 'Gobo',
  effect: 'Effect',
  speed: 'Speed',
  mode: 'Mode',
};

/** Plain-words name for a capability; the raw name for anything unlisted. */
export function capabilityLabel(c: string): string {
  return CAP_LABEL[c] ?? c;
}

/** What the chosen fixture - or the chosen zone of it - can control. */
export function laneLightCapabilities(t: { fixtureId: string; subfixtureIndex?: number | undefined }): FixtureCapabilityName[] {
  const fx = findLaneFixture(t.fixtureId);
  if (!fx) return [];
  if (t.subfixtureIndex !== undefined) {
    return fixtureZones(fx).find((z) => z.index === t.subfixtureIndex)?.capabilities ?? [];
  }
  return fixtureCapabilityNames(fx);
}

function preferred(caps: FixtureCapabilityName[], keep?: FixtureCapabilityName): FixtureCapabilityName | undefined {
  if (keep && caps.includes(keep)) return keep;
  return caps.includes('intensity') ? 'intensity' : caps[0];
}

/** The target a lane gets when it is switched to Light: the first patched
 *  fixture that can do anything, on Brightness when it has it. `null` when no
 *  light is patched - the caller then explains rather than inventing one. */
export function defaultLaneDmxTarget(): LaneDmxTarget | null {
  for (const fx of fixtures()) {
    const cap = preferred(fixtureCapabilityNames(fx));
    if (cap) return { type: 'dmx', fixtureId: fx.id, capability: cap };
  }
  return null;
}

/** Short human description of a light lane target, e.g. "Wash L zone 2 - Red". */
export function describeLaneLight(t: LaneDmxTarget): string {
  const fx = findLaneFixture(t.fixtureId);
  if (!fx) return 'light removed';
  const zone = t.subfixtureIndex !== undefined
    ? fixtureZones(fx).find((z) => z.index === t.subfixtureIndex)?.name ?? `zone ${t.subfixtureIndex + 1}`
    : '';
  return `${fx.name}${zone ? ` ${zone}` : ''} - ${capabilityLabel(t.capability)}`;
}

const key = (fixtureId: string, zone?: number) => (zone === undefined ? fixtureId : `${fixtureId}#${zone}`);

export function LaneLightPicker(props: {
  target: LaneDmxTarget;
  onChange: (next: LaneDmxTarget) => void;
  rowClass: string;
  labelClass: string;
  selectClass: string;
}) {
  const current = () => key(props.target.fixtureId, props.target.subfixtureIndex);
  const missing = () => !findLaneFixture(props.target.fixtureId);
  const caps = () => laneLightCapabilities(props.target);

  function pickLight(value: string): void {
    const [fixtureId, zone] = value.split('#') as [string, string | undefined];
    const subfixtureIndex = zone === undefined ? undefined : Number(zone);
    const nextCaps = laneLightCapabilities({ fixtureId, subfixtureIndex });
    const capability = preferred(nextCaps, props.target.capability) ?? props.target.capability;
    props.onChange({ type: 'dmx', fixtureId, capability, ...(subfixtureIndex !== undefined ? { subfixtureIndex } : {}) });
  }

  return (
    <>
      <label class={props.rowClass} data-lane-light="fixture">
        <span class={props.labelClass}>Light</span>
        <select class={props.selectClass} value={current()} onChange={(e) => pickLight(e.currentTarget.value)}>
          <Show when={missing()}>
            <option value={current()} disabled>light removed</option>
          </Show>
          <For each={fixtures()}>
            {(fx) => (
              <>
                <option value={key(fx.id)}>{fx.name}</option>
                <For each={fixtureZones(fx)}>
                  {(z) => <option value={key(fx.id, z.index)}>{`${fx.name} - ${z.name}`}</option>}
                </For>
              </>
            )}
          </For>
        </select>
      </label>
      <label class={props.rowClass} data-lane-light="capability">
        <span class={props.labelClass}>Controls</span>
        <select
          class={props.selectClass}
          value={props.target.capability}
          onChange={(e) => props.onChange({ ...props.target, capability: e.currentTarget.value as FixtureCapabilityName })}
        >
          <Show when={!caps().includes(props.target.capability)}>
            <option value={props.target.capability} disabled>{`${capabilityLabel(props.target.capability)} (not on this light)`}</option>
          </Show>
          <For each={caps()}>{(c) => <option value={c}>{capabilityLabel(c)}</option>}</For>
        </select>
      </label>
    </>
  );
}
